import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, getDocs, orderBy, query, where, updateDoc, doc, increment, serverTimestamp } from 'firebase/firestore';
import { Container, Card, Button, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [likedPosts, setLikedPosts] = useState({});

  const navigate = useNavigate();

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const savedLikes = JSON.parse(localStorage.getItem('likedNews') || '{}');
    setLikedPosts(savedLikes);
  }, []);

  const translations = {
    'News': 'الأخبار',
    'Latest News & Updates': 'أحدث الأخبار والتحديثات',
    'Read More': 'اقرأ المزيد',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'No news found': 'لم يتم العثور على أخبار',
    'Error loading news': 'خطأ في تحميل الأخبار',
    'Loading news...': 'جاري تحميل الأخبار...',
    'By': 'بواسطة',
    'Published on': 'نشر في',
    'Breaking News': 'خبر عاجل',
    'Official Statement': 'بيان رسمي',
    'Field Report': 'تقرير ميداني',
    'Like': 'أعجبني',
    'Liked': 'معجب به',
    'Thank you for your like!': 'شكراً لإعجابك!',
    'You already liked this news': 'لقد أعجبت بهذا الخبر مسبقاً'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  // MODIFIED: Always use Arabic font for both languages
  const getFontFamily = (weight = 400) => {
    return `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #F0F0F0 0%, #FFFFFF 50%, #F0F0F0 100%)',
      padding: '0',
      fontFamily: getFontFamily(),
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh',
      color: '#32373B'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem 2rem',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid #D3BA80',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#32373B',
      padding: '120px 20px 60px',
      background: `linear-gradient(135deg, 
        rgba(211, 186, 128, 0.1) 0%, 
        rgba(211, 186, 128, 0.2) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '3px solid #D3BA80'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      color: '#255853',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      letterSpacing: '-0.5px',
      lineHeight: '1.2',
      position: 'relative',
      fontFamily: getFontFamily(700)
    },
    titleAccent: {
      display: 'block',
      width: '80px',
      height: '4px',
      background: 'linear-gradient(90deg, #D3BA80, #E0CFA6)',
      margin: '1rem auto',
      borderRadius: '2px'
    },
    subtitle: {
      fontSize: '1.3rem',
      marginBottom: '2rem',
      color: '#357E77',
      fontWeight: '500',
      maxWidth: '800px',
      margin: '0 auto 2rem',
      lineHeight: '1.5',
      fontFamily: getFontFamily(500)
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 88, 83, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: getFontFamily(500)
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(211, 186, 128, 0.08)',
      borderRadius: '50%'
    },
    footerSection: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      padding: '3rem 2rem',
      marginTop: '4rem',
      textAlign: 'center',
      fontFamily: getFontFamily(500)
    },
    authorProfile: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      objectFit: 'cover',
      marginRight: '8px',
      border: '1px solid rgba(211, 186, 128, 0.3)'
    }
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Starting to fetch news...');
        console.log('Firebase db1:', db1);
        
        if (!db1) {
          const errorMsg = 'Firebase db1 is not properly initialized. Check your firebase config.';
          console.error(errorMsg);
          throw new Error(errorMsg);
        }
        
        let newsQuery;
        try {
          newsQuery = query(
            collection(db1, 'news'),
            orderBy('createdAt', 'desc')
          );
          console.log('Query created successfully');
        } catch (queryError) {
          console.error('Error creating query:', queryError);
          throw new Error(`Query error: ${queryError.message}`);
        }
        
        let querySnapshot;
        try {
          querySnapshot = await getDocs(newsQuery);
          console.log('Query executed successfully, documents found:', querySnapshot.size);
        } catch (fetchError) {
          console.error('Error executing query:', fetchError);
          throw new Error(`Fetch error: ${fetchError.message}`);
        }
        
        const newsList = [];
        
        querySnapshot.forEach((doc) => {
          console.log('Processing document:', doc.id);
          const data = doc.data();
          console.log('Document data:', data);
          
          let createdAtDate;
          try {
            if (data.createdAt && typeof data.createdAt.toDate === 'function') {
              createdAtDate = data.createdAt.toDate();
            } else if (data.createdAt instanceof Date) {
              createdAtDate = data.createdAt;
            } else if (data.createdAt && typeof data.createdAt === 'string') {
              createdAtDate = new Date(data.createdAt);
            } else if (data.createdAt && typeof data.createdAt === 'number') {
              createdAtDate = new Date(data.createdAt);
            } else {
              createdAtDate = new Date();
              console.warn('No valid createdAt found, using current date for document:', doc.id);
            }
          } catch (dateError) {
            console.error('Error processing date for document:', doc.id, dateError);
            createdAtDate = new Date();
          }

          if (isNaN(createdAtDate.getTime())) {
            console.warn('Invalid date for document:', doc.id, 'using current date');
            createdAtDate = new Date();
          }
          
          const newsItem = {
            id: doc.id,
            title: data?.title || 'No Title',
            excerpt: data?.excerpt || data?.content?.substring(0, 150) + '...' || 'No content available',
            content: data?.content || 'No content available',
            author: data?.author || 'مؤسسة إرضاء الإعلامية',
            category: data?.category || 'Breaking News',
            featuredImage: data?.featuredImage || data?.image || null,
            token: data?.token || doc.id,
            views: parseInt(data?.views) || 0,
            likes: parseInt(data?.likes) || 0,
            createdAt: createdAtDate,
            ...data
          };
          
          newsList.push(newsItem);
        });

        console.log('Final news list with dates:', newsList.map(item => ({
          id: item.id,
          title: item.title,
          createdAt: item.createdAt,
          formattedDate: formatDate(item.createdAt)
        })));
        
        setNews(newsList);
        
        if (newsList.length === 0) {
          console.log('No news documents found in the collection');
        }
        
      } catch (err) {
        console.error('Error fetching news:', err);
        const errorMessage = `${translateText('Error loading news')}: ${err.message}`;
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const handleLike = async (newsId, token) => {
    if (!newsId || !token) {
      console.error('Missing newsId or token for like action');
      return;
    }

    if (likedPosts[token]) {
      alert(translateText('You already liked this news'));
      return;
    }

    try {
      console.log('Liking news:', newsId, token);
      
      await updateDoc(doc(db1, 'news', newsId), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      setNews(prev => prev.map(item => 
        item.id === newsId ? { ...item, likes: (item.likes || 0) + 1 } : item
      ));

      const newLikedPosts = { ...likedPosts, [token]: true };
      setLikedPosts(newLikedPosts);
      localStorage.setItem('likedNews', JSON.stringify(newLikedPosts));

      alert(translateText('Thank you for your like!'));
    } catch (err) {
      console.error('Error updating likes:', err);
      alert('Error updating likes. Please try again.');
    }
  };

  const handleReadMore = async (newsId, token) => {
    if (!newsId || !token) {
      console.error('Missing newsId or token for read more action');
      navigate('/news');
      return;
    }

    try {
      console.log('Reading more:', newsId, token);
      
      await updateDoc(doc(db1, 'news', newsId), {
        views: increment(1),
        lastViewed: serverTimestamp()
      });

      setNews(prev => prev.map(item => 
        item.id === newsId ? { ...item, views: (item.views || 0) + 1 } : item
      ));

      navigate(`/news/${token}`);
    } catch (err) {
      console.error('Error incrementing views:', err);
      navigate(`/news/${token}`);
    }
  };

  const formatDate = (date) => {
    if (!date) {
      console.warn('No date provided to formatDate');
      return 'Unknown date';
    }
    
    try {
      let dateObj;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (date && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else if (typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        console.warn('Unknown date format:', date);
        return 'Invalid date';
      }

      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date object:', dateObj);
        return 'Invalid date';
      }

      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };

      if (isArabic) {
        return dateObj.toLocaleDateString('ar-SA', options);
      } else {
        return dateObj.toLocaleDateString('en-US', options);
      }
    } catch (err) {
      console.error('Error formatting date:', err, 'Original date:', date);
      return 'Date error';
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const getCategoryTranslation = (category) => {
    if (!category) return translateText('Breaking News');
    return translations[category] || category;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div className="text-center py-5">
            <Spinner 
              animation="border" 
              style={{ 
                color: '#D3BA80', 
                width: '3rem', 
                height: '3rem' 
              }} 
            />
            <p className="mt-3" style={{ color: '#357E77', fontSize: '1.1rem', fontFamily: getFontFamily(500) }}>
              {translateText('Loading news...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && news.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <Alert variant="light" className="text-center" style={{ 
            border: '1px solid #D3BA80', 
            color: '#32373B',
            fontFamily: getFontFamily(400) 
          }}>
            <div className="d-flex align-items-center justify-content-center mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '2rem', color: '#D3BA80' }}></i>
              <h5 className="mb-0" style={{ color: '#32373B', fontFamily: getFontFamily(600) }}>{translateText('Error loading news')}</h5>
            </div>
            <p className="mb-3">{error}</p>
            <Button 
              variant="outline-dark" 
              onClick={() => window.location.reload()}
              style={{ 
                borderColor: '#255853', 
                color: '#255853',
                fontFamily: getFontFamily(500)
              }}
            >
              Retry Loading
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(211, 186, 128, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(53, 126, 119, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(224, 207, 166, 0.06)'}}></div>
      </div>

      <div style={styles.languageToggle}>
        <button 
          onClick={() => setIsArabic(!isArabic)}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#D3BA80';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(211, 186, 128, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#255853';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 88, 83, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('News')}</h1>
        <div style={styles.titleAccent}></div>
        <p style={styles.subtitle}>
          {translateText('Latest News & Updates')}
        </p>
      </header>

      <div style={styles.content}>
        {error && (
          <Alert variant="light" className="mb-4" style={{ 
            border: '1px solid #D3BA80', 
            color: '#32373B',
            fontFamily: getFontFamily(400) 
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#D3BA80' }}></i>
              <span>{error}</span>
            </div>
          </Alert>
        )}

        {news.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '4rem', color: '#dee2e6', marginBottom: '1rem' }}>
              📰
            </div>
            <h4 style={{ color: '#357E77', fontFamily: getFontFamily(600) }}>{translateText('No news found')}</h4>
            <p className="text-muted" style={{ fontFamily: getFontFamily(400) }}>Check back later for the latest updates</p>
            <Button 
              variant="outline-dark" 
              onClick={() => window.location.reload()}
              className="mt-2"
              style={{ 
                borderColor: '#255853', 
                color: '#255853',
                fontFamily: getFontFamily(500)
              }}
            >
              Refresh Page
            </Button>
          </div>
        ) : (
          <Row>
            {news.map((item, index) => (
              <Col key={item.id} lg={6} className="mb-4">
                <Card 
                  className="h-100 shadow-sm" 
                  style={{ 
                    border: '1px solid #E0CFA6',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    transform: hoveredCard === index ? 'translateY(-5px)' : 'translateY(0)',
                    boxShadow: hoveredCard === index ? '0 8px 25px rgba(211, 186, 128, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {item.featuredImage && (
                    <Card.Img 
                      variant="top" 
                      src={item.featuredImage}
                      style={{ 
                        height: '200px', 
                        objectFit: 'cover',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                      alt={item.title}
                    />
                  )}
                  <Card.Body className="d-flex flex-column" style={{ backgroundColor: '#ffffff' }}>
                    <div className="mb-2">
                      <Badge 
                        style={{ 
                          fontSize: '0.8rem',
                          backgroundColor: '#255853',
                          color: 'white',
                          fontFamily: getFontFamily(500)
                        }}
                      >
                        {getCategoryTranslation(item.category)}
                      </Badge>
                    </div>
                    
                    <Card.Title style={{ 
                      color: '#32373B',
                      fontWeight: '600',
                      lineHeight: '1.4',
                      minHeight: '68px',
                      fontSize: '1.3rem',
                      fontFamily: getFontFamily(600)
                    }}>
                      {item.title}
                    </Card.Title>

                    <Card.Text style={{ 
                      color: '#5d6d7e',
                      flex: 1,
                      lineHeight: '1.6',
                      minHeight: '48px',
                      fontFamily: getFontFamily(400)
                    }}>
                      {truncateText(item.excerpt, 120)}
                    </Card.Text>

                    <div className="mt-auto">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small style={{ 
                          color: '#7f8c8d', 
                          fontWeight: '500', 
                          display: 'flex', 
                          alignItems: 'center',
                          fontFamily: getFontFamily(500)
                        }}>
                          <img 
                            src="/al-majd.png" 
                            alt="مؤسسة إرضاء الإعلامية"
                            style={styles.authorProfile}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {translateText('By')} {item.author}
                        </small>
                        <small style={{ color: '#7f8c8d', fontFamily: getFontFamily(400) }}>
                          <i className="bi bi-calendar me-1"></i>
                          {formatDate(item.createdAt)}
                        </small>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-3" style={{ color: '#7f8c8d', fontFamily: getFontFamily(400) }}>
                          <small>
                            <i className="bi bi-eye me-1"></i>
                            {item.views} {translateText('Views')}
                          </small>
                          <small>
                            <i className="bi bi-hand-thumbs-up me-1"></i>
                            {item.likes} {translateText('Likes')}
                          </small>
                        </div>
                        <div className="d-flex gap-2">
                          <Button 
                            variant={likedPosts[item.token] ? "success" : "outline-dark"}
                            size="sm"
                            style={{ 
                              borderRadius: '20px',
                              fontWeight: '500',
                              fontFamily: getFontFamily(500),
                              ...(likedPosts[item.token] ? {
                                backgroundColor: '#27ae60',
                                borderColor: '#27ae60'
                              } : {
                                color: '#255853',
                                borderColor: '#255853'
                              })
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(item.id, item.token);
                            }}
                            disabled={likedPosts[item.token]}
                          >
                            <i className={`bi ${likedPosts[item.token] ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-1`}></i>
                            {likedPosts[item.token] ? translateText('Liked') : translateText('Like')}
                          </Button>
                          <Button 
                            variant="outline-dark"
                            size="sm"
                            style={{ 
                              color: '#255853', 
                              borderColor: '#255853',
                              borderRadius: '20px',
                              fontWeight: '500',
                              fontFamily: getFontFamily(500)
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadMore(item.id, item.token);
                            }}
                          >
                            {translateText('Read More')}
                            <i className="bi bi-arrow-left ms-2" style={isArabic ? {} : { transform: 'scaleX(-1)' }}></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <footer style={styles.footerSection}>
        <Container>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            © 2024 {isArabic ? 'مؤسسة إرضاء الإعلامية' : 'مؤسسة إرضاء الإعلامية'}. {isArabic ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default NewsPage;
